/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer, Button, Select, MenuItem } from '@mui/material';
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
import useAuth from '../../../hooks/useAuth';
import cssStyles from '../../../utils/cssStyles';
import { NAVBAR } from '../../../config';
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/other/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
import navConfig from './NavConfig';
import NavbarAccount from './NavbarAccount';
import CollapseButton from './CollapseButton';
import packageJson from '../../../../package.json';
import { useGetRoomListQuery } from '../../../generated/graphql';
import * as Sentry from '@sentry/react';
import { getActiveRoomIdAndName } from '../../../utils/getActiveRoomIdAndName';

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

type Props = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({
  isOpenSidebar,
  onCloseSidebar,
}: Props) {
  const { logout, activeRoomName, setActiveRoom, jwtDecoded } = useAuth();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');

  const { isCollapse, collapseClick, onToggleCollapse } = useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname]);

  const [{ error: errorRoomListData, data: roomListData }] =
    useGetRoomListQuery({});

  useEffect(() => {
    if (errorRoomListData) {
      toast.error('Error fetching room list data');
    }
  }, [errorRoomListData]);

  const roomList = roomListData?.getRoomList;

  useEffect(() => {
    const { activeRoomId, activeRoomName } = getActiveRoomIdAndName();
    if (activeRoomId && activeRoomName) {
      setActiveRoom(activeRoomName, activeRoomId);
    } else if (roomList?.length) {
      setActiveRoom(roomList[0]?.name, roomList[0]?.id);
    }
  }, [roomList]);

  const handleChangeActiveRoom = (event: any) => {
    const selectedRoomName = event.target.value;
    const selectedRoom = roomList?.find(
      (room: any) => room?.name === selectedRoomName
    );
    if (!selectedRoom) return;
    setActiveRoom(selectedRoom.name, selectedRoom.id);
  };

  useEffect(() => {
    if (jwtDecoded) {
      Sentry.setUser({
        id: jwtDecoded.adminId,
        name: jwtDecoded?.name,
        role: jwtDecoded.adminRoleName,
      });
    }
  }, [jwtDecoded]);

  const handleToggleCollapse = () => {
    onToggleCollapse();
  };

  const renderContent = useMemo(
    () => (
      <Scrollbar
        sx={{
          backgroundColor: isLight ? '#fbfbfb' : '#20272f',
          height: 1,
          '& .simplebar-content': {
            height: 1,
            display: 'inline',
            flexDirection: 'column',
          },
        }}
      >
        <Stack
          spacing={3}
          sx={{
            pt: 3,
            pb: 2,
            px: 2.5,
            flexShrink: 0,
            ...(isCollapse && { alignItems: 'center' }),
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            {!isCollapse && <Logo title />}
            {isDesktop && (
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggleCollapse();
                }}
              >
                <CollapseButton
                  onToggleCollapse={handleToggleCollapse}
                  collapseClick={collapseClick}
                />
              </Box>
            )}
          </Stack>

          {!isCollapse && (
            <NavbarAccount isCollapse={isCollapse} operator={jwtDecoded} />
          )}

          {!isCollapse && roomList && (
            <Box sx={{ m: 'auto' }}>
              <Select
                size="small"
                sx={{
                  backgroundColor: '#2e3c5a',
                  color: '#fff',
                }}
                fullWidth
                disabled={roomList.length < 2}
                value={activeRoomName}
                onChange={handleChangeActiveRoom}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#2e3c5a',
                      color: '#fff',
                      maxHeight: 300,
                      scrollbarColor: '#fff #2e3c5a',
                    },
                  },
                }}
              >
                {roomList.map((room: any) => (
                  <MenuItem key={room.id} value={room.name}>
                    {room.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Stack>

        <NavSectionVertical navConfig={navConfig} isCollapse={isCollapse} />

        <Box sx={{ flexGrow: 1 }} />

        {!isCollapse && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              m: 2,
            }}
          >
            <Button
              onClick={logout}
              size="large"
              variant="outlined"
              fullWidth
              color="inherit"
            >
              Logout
            </Button>
          </Box>
        )}

        {!isCollapse && (
          <Box
            sx={{
              color: '#949494',
              fontSize: '12px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 1.5,
            }}
          >
            Version {packageJson.version}
          </Box>
        )}
      </Scrollbar>
    ),
    [
      isLight,
      isCollapse,
      isDesktop,
      collapseClick,
      jwtDecoded,
      activeRoomName,
      roomList,
    ]
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && { position: 'absolute' }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: isCollapse
                ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
                : NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'background.default',
              transition: theme.transitions.create(
                ['width', 'background-color'],
                {
                  duration: theme.transitions.duration.shorter,
                  easing: theme.transitions.easing.easeInOut,
                }
              ),
              ...cssStyles(theme),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
